export interface UploadFailure {
  fileName: string;
  message: string;
}

export interface UploadBatchOutcome<T> {
  results: T[];
  failures: UploadFailure[];
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Upload failed';
}

export async function uploadBatch<T>(
  files: File[],
  uploadOne: (file: File) => Promise<T>,
): Promise<UploadBatchOutcome<T>> {
  const results: T[] = [];
  const failures: UploadFailure[] = [];
  for (const file of files) {
    try {
      results.push(await uploadOne(file));
    } catch (error) {
      failures.push({ fileName: file.name, message: messageOf(error) });
    }
  }
  return { results, failures };
}

export function describeUploadFailures(failures: UploadFailure[]): string {
  return failures
    .map((f) => (f.message.includes(f.fileName) ? f.message : `${f.fileName}: ${f.message}`))
    .join(' ');
}

const MAX_NAMED_FAILURES = 2;

export function describeUploadFailuresCapped(
  failures: UploadFailure[],
  max: number = MAX_NAMED_FAILURES,
): string {
  if (failures.length === 0) return '';
  if (failures.length === 1) {
    const text = describeUploadFailures(failures);
    return /[.!?]$/.test(text) ? text : `${text}.`;
  }
  const named = failures.slice(0, max).map((f) => f.fileName);
  const hidden = failures.length - named.length;
  const list =
    hidden > 0
      ? `${named.join(', ')} and ${hidden} more`
      : named.length === 2
        ? `${named[0]} and ${named[1]}`
        : named.join(', ');
  return `Not uploaded: ${list}.`;
}

export function describeUploadWarning({
  uploaded,
  failures,
  conversions,
}: {
  uploaded: number;
  failures: UploadFailure[];
  conversions: Array<{ from: string; to: string }>;
}): string {
  return [
    `${uploaded} uploaded.`,
    describeUploadFailuresCapped(failures),
    summarizeConversionFormats(conversions),
  ]
    .filter(Boolean)
    .join(' ');
}

export function uploadFailureTitle(failed: number, total: number): string {
  if (total === 1) return 'Upload failed';
  if (failed === total) return `None of the ${total} files were uploaded`;
  return `${failed} of ${total} files were not uploaded`;
}

export function summarizeConversionFormats(
  conversions: Array<{ from: string; to: string }>,
): string | null {
  if (conversions.length === 0) return null;
  const groups = new Map<string, number>();
  for (const { from, to } of conversions) {
    const key = `${from} saved as ${to}`;
    groups.set(key, (groups.get(key) ?? 0) + 1);
  }
  if (groups.size > 2) {
    return `${conversions.length} files saved in a different format.`;
  }
  return `${[...groups].map(([label, n]) => `${n} ${label}`).join(', ')}.`;
}
